/**
 * The Assign wizard's rules, kept out of the screen so they read in one place
 * (plan docs/plans/lms-plan.md 8.2 "The Assign wizard", 5.4, 6.5, 11.2,
 * 18.16; WP-43).
 */
import type { CourseCategory } from '../../../content/types';
import type { AdminCourseItem } from '../../../api/assignments';
import type { CampaignAudience, SendCampaignInput, WizardCampaignKind } from '../../../api/campaigns';
import type { LmsPlatformRole } from '../../../api/client';
import { roleLabel } from '../../../components/pickers/shared';

/** The four steps of 8.2, in order. */
export const WIZARD_STEPS = ['Course', 'Audience', 'Due date and message', 'Review and send'] as const;
export type WizardStep = 0 | 1 | 2 | 3;

/**
 * The campaign kind a send carries (5.4; the wizard sends manual, scenario or
 * sop_update only, 18.16): a best-practices scenario is a scenario refresher
 * (6.5, R8), an SOP course is an SOP update (11.2), anything else is manual.
 */
export function kindForCategory(category: CourseCategory | null | undefined): WizardCampaignKind {
  if (category === 'best_practices') return 'scenario';
  if (category === 'sop') return 'sop_update';
  return 'manual';
}

/** The audience's selections in words for the review (the preview says who they reach). */
export function describeAudience(audience: CampaignAudience, siteNames: Record<string, string>): string[] {
  const out: string[] = [];
  if (audience.everyone_at_sites.length > 0) {
    out.push(`Everyone at ${audience.everyone_at_sites.map((id) => siteNames[id] ?? 'a site').join(', ')}`);
  }
  if (audience.platform_roles.length > 0) {
    out.push(`Roles at this site: ${audience.platform_roles.map((r: LmsPlatformRole) => roleLabel(r)).join(', ')}`);
  }
  if (audience.group_ids.length > 0) {
    const n = audience.group_ids.length;
    out.push(`${n.toLocaleString()} ${n === 1 ? 'group' : 'groups'}`);
  }
  if (audience.user_ids.length > 0) {
    const n = audience.user_ids.length;
    out.push(`${n.toLocaleString()} named ${n === 1 ? 'person' : 'people'}`);
  }
  return out;
}

/**
 * The POST /admin/campaigns body (Appendix A plus exclude_user_ids, 18.16):
 * the course, the pinned version when pinned, the kind, the audience as
 * selected, the due window, the message, force, and the people unticked in
 * the preview (only those still in it).
 */
export function buildSendInput(input: {
  course: AdminCourseItem;
  pinVersion: boolean;
  audience: CampaignAudience;
  dueDays: number;
  message: string;
  force: boolean;
  excluded: string[];
  previewIds: string[] | null;
}): SendCampaignInput {
  const inPreview = input.previewIds ? new Set(input.previewIds) : null;
  return {
    course_id: input.course.id,
    course_version_id: input.pinVersion && input.course.current_version ? input.course.current_version.id : null,
    kind: kindForCategory(input.course.category),
    audience: {
      everyone_at_sites: [...input.audience.everyone_at_sites],
      platform_roles: [...input.audience.platform_roles],
      group_ids: [...input.audience.group_ids],
      user_ids: [...input.audience.user_ids],
    },
    due_days: input.dueDays,
    message: input.message.trim() || null,
    force: input.force,
    exclude_user_ids: input.excluded.filter((id) => !inPreview || inPreview.has(id)),
  };
}

/** The due date a window of `days` gives from `now` (the server computes the stored one, 5.4). */
export function dueDateFrom(now: Date, days: number): string {
  return new Date(now.getTime() + days * 24 * 60 * 60 * 1000).toISOString();
}
